import React from 'react';
import { BrowserRouter } from 'react-router-dom';
import { ThemeProvider } from './contexts/ThemeContext';
import { ApolloProvider } from '@apollo/client';
import { AppearanceProvider } from './contexts/AppearanceProvider';
import { KeycloakAuthProvider } from './contexts/KeycloakAuthProvider';
import { ApiProvider, useApi } from './contexts/ApiContext';
import { AdminSocketProvider } from './contexts/AdminSocketContext';
import { UploadProvider } from './contexts/UploadContext';
import { KGSocketProvider } from './contexts/KGSocketContext';
import { NotificationProvider } from './components/Notification/NotificationContext';
import { KGImportProgressProvider } from './contexts/KGImportProgressContext';
import { TaskManagerProvider } from './contexts/TaskManagerContext';
import { NotificationContainer } from './components/Notification';
import { KGImportGlobalIndicator } from './components/common';
import Router from './router/router';
import { I18nProvider } from './modules/i18n';
import './index.css';
import { loadDevMessages, loadErrorMessages } from '@apollo/client/dev';

// Enable Apollo detailed dev error messages (development only)
if (process.env.NODE_ENV !== 'production') {
  loadDevMessages();
  loadErrorMessages();
}

// Error Boundary Component
class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean }> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_error: Error) {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className='flex min-h-screen items-center justify-center bg-gray-50 dark:bg-dark-background'>
          <div className='text-center'>
            <h1 className='mb-4 text-2xl font-bold text-gray-900 dark:text-dark-text'>Đã xảy ra lỗi</h1>
            <p className='mb-4 text-gray-600 dark:text-dark-text-secondary'>Vui lòng tải lại trang để tiếp tục.</p>
            <button
              onClick={() => window.location.reload()}
              className='rounded-lg bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700'
            >
              Tải lại trang
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Inner component that uses Apollo client from API context
const AppContent: React.FC = () => {
  const { apolloClient } = useApi();

  return (
    <ApolloProvider client={apolloClient}>
      <TaskManagerProvider>
        <KGImportProgressProvider>
          <AdminSocketProvider>
            <UploadProvider>
              <KGSocketProvider>
                <div className='App'>
                  <KGImportGlobalIndicator />
                  <Router />
                  <NotificationContainer />
                </div>
              </KGSocketProvider>
            </UploadProvider>
          </AdminSocketProvider>
        </KGImportProgressProvider>
      </TaskManagerProvider>
    </ApolloProvider>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ThemeProvider>
          <I18nProvider>
            <AppearanceProvider>
              <NotificationProvider>
                <KeycloakAuthProvider>
                  <ApiProvider>
                    <AppContent />
                  </ApiProvider>
                </KeycloakAuthProvider>
              </NotificationProvider>
            </AppearanceProvider>
          </I18nProvider>
        </ThemeProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
